/* src/components/ui/LoadingSpinner.js */
import React from 'react';

/**
 * A flexible loading spinner component that can adapt to different contexts.
 * 
 * @param {Object} props - Component props
 * @param {string} props.message - Optional message to display below the spinner
 * @param {boolean} props.small - Whether to render a smaller version of the spinner
 * @param {boolean} props.fullScreen - Whether to render the spinner fullscreen
 * @param {string} props.className - Additional CSS classes to apply
 * @param {number} props.containerHeight - Optional fixed height for the container (useful for consistent layout)
 */
const LoadingSpinner = ({
  message,
  small = false,
  fullScreen = false,
  className = "",
  containerHeight = null,
}) => {
  // Base container classes that adapt to context
  const containerClasses = fullScreen 
    ? "h-screen flex items-center justify-center bg-gray-100 p-4"
    : `flex items-center justify-center p-4 ${className}`;
  
  // Apply fixed height if provided
  const containerStyle = containerHeight 
    ? { height: containerHeight } 
    : {};
  
  // Size classes for the spinner SVG
  const spinnerSize = small ? "h-4 w-4" : "h-8 w-8";
  
  return (
    <div className={containerClasses} style={containerStyle}>
      <div className="flex flex-col items-center">
        <svg 
          className={`animate-spin ${spinnerSize} text-indigo-600 mb-2`} 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {message && (
          <p className={`${small ? 'text-sm' : 'text-base sm:text-lg'} text-gray-600`} role="status">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default LoadingSpinner;